// pages/monitor/panels/ems/EmsCurve.js
// 《云平台（能源管家）需求文档》2.7.5 曲线报表（p23–28）
// 差异 #1：综合效率 = 总放电量 / 总充电量
// 差异 #2：实时运行时间轴为「今日 0 点 ~ 次日 6 点」
// 差异 #5：本类型无电池簇曲线
import React, { useState } from 'react';
import { useLang, t } from '../../consoles/ems/langContext';
import { Table, Select, Button, Segmented, Tag, message } from 'antd';
import ScrollTabs from '../../../../components/ScrollTabs';
import {
  ResponsiveContainer, ComposedChart, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from 'recharts';
import {
  getStation, getEnergyReport, getIncomeReport, getMeterReport, getPowerCurve,
  getPcsCurve, getRackCurve, getCellCurve, getClusterTempReport, getClusterVoltageReport, getExportList,
} from '../../../../config/stationData';
import { DownloadOutlined } from '@ant-design/icons';
import { PCS_TREND, RACK_2D, CELL_PICKS, CELL_TEMP, CELL_SOC, METER_STOP, CUSTOM_POINTS, CUSTOM_FREQ } from './data';

const tick = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const grid = 'rgba(255,255,255,0.08)';
const tooltipStyle = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const legendStyle = { color: 'rgba(255,255,255,0.7)', fontSize: 12 };

const sum = (rows, key) => rows.reduce((a, r) => a + r[key], 0);
const fmt = (n, d = 1) => Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

function Div({ children }) { return <div className="panel">{children}</div>; }

// ---------- 概览统计 ----------
function Overview({ st }) {
  const { lang } = useLang();
  const inc = getIncomeReport();
  const eng = getEnergyReport();
  const income = sum(inc, 'total');
  const charge = sum(eng, 'charge');
  const discharge = sum(eng, 'discharge');
  const efficiency = charge > 0 ? ((discharge / charge) * 100).toFixed(1) : '—';
  return (
    <>
      <div className="kpi-grid kpi-grid-5">
        <div className="kpi-card"><div className="kpi-label">{t(lang, '当前并网点收益（近七天）', 'Grid Point Income (Last 7 Days)')}</div><div className="kpi-value">{fmt(income, 0)}<span className="unit"> {t(lang, '元', 'CNY')}</span></div></div>
        <div className="kpi-card"><div className="kpi-label">{t(lang, '充电量（近七天）', 'Charge (Last 7 Days)')}</div><div className="kpi-value">{fmt(charge, 0)}<span className="unit"> kWh</span></div></div>
        <div className="kpi-card"><div className="kpi-label">{t(lang, '放电量（近七天）', 'Discharge (Last 7 Days)')}</div><div className="kpi-value">{fmt(discharge, 0)}<span className="unit"> kWh</span></div></div>
        <div className="kpi-card"><div className="kpi-label">{t(lang, '系统效率', 'System Efficiency')}</div><div className="kpi-value">{efficiency}<span className="unit"> %</span></div><div className="kpi-foot">{t(lang, '总放电量 / 总充电量', 'Total Discharge / Total Charge')}</div></div>
        <div className="kpi-card"><div className="kpi-label">{t(lang, '电站类型', 'Station Type')}</div><div className="kpi-value" style={{ fontSize: 20 }}>{st.typeLabel}</div></div>
      </div>
      <div className="grid-2">
        <Div>
          <div className="panel-title"><span>{t(lang, '收益趋势', 'Income Trend')}</span><small>{t(lang, '近七天', 'Last 7 Days')}</small></div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={inc} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="day" tick={tick} stroke={grid} />
              <YAxis tick={tick} stroke={grid} unit={t(lang, '元', 'CNY')} width={64} />
              <RTooltip contentStyle={tooltipStyle} />
              <Line type="linear" dataKey="total" name={t(lang, '收益', 'Income')} stroke="#52e8b0" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Div>
        <Div>
          <div className="panel-title"><span>{t(lang, '充放电量趋势', 'Charge & Discharge Trend')}</span><small>{t(lang, '近七天 · 系统效率', 'Last 7 Days · System Efficiency')} {efficiency}%</small></div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={eng} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="day" tick={tick} stroke={grid} />
              <YAxis tick={tick} stroke={grid} unit="kWh" width={64} />
              <RTooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="linear" dataKey="charge" name={t(lang, '充电量', 'Charge')} stroke="#4dd8ff" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="linear" dataKey="discharge" name={t(lang, '放电量', 'Discharge')} stroke="#52e8b0" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Div>
      </div>
    </>
  );
}

// ---------- 收益报表 ----------
function IncomeReport() {
  const { lang } = useLang();
  const inc = getIncomeReport();
  const data = inc.map((r) => ({ ...r, actual: r.peakDischarge - r.valleyCharge }));
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '收益报表', 'Income Report')}</span><small>{t(lang, '日充电成本 / 日放电收益 / 实际收益', 'Daily Charge Cost / Daily Discharge Income / Net Income')}</small></div>
      <Table
        size="small" rowKey="key" pagination={false}
        columns={[
          { title: t(lang, '日期', 'Date'), dataIndex: 'day' },
          { title: t(lang, '日充电成本 (元)', 'Daily Charge Cost (CNY)'), dataIndex: 'valleyCharge', align: 'right' },
          { title: t(lang, '日放电收益 (元)', 'Daily Discharge Income (CNY)'), dataIndex: 'peakDischarge', align: 'right' },
          { title: t(lang, '实际收益 (元)', 'Net Income (CNY)'), dataIndex: 'actual', align: 'right' },
        ]}
        dataSource={data}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>{t(lang, '合计', 'Total')}</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">{sum(data, 'valleyCharge')}</Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">{sum(data, 'peakDischarge')}</Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">{sum(data, 'actual')}</Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      <div className="ems-note">{t(lang, '实际收益 = 日放电收益 − 日充电成本。', 'Net Income = Daily Discharge Income − Daily Charge Cost.')}</div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 16, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="day" tick={tick} stroke={grid} />
          <YAxis tick={tick} stroke={grid} unit={t(lang, '元', 'CNY')} width={64} />
          <RTooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={legendStyle} />
          <Line type="linear" dataKey="valleyCharge" name={t(lang, '日充电成本', 'Daily Charge Cost')} stroke="#4dd8ff" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="linear" dataKey="peakDischarge" name={t(lang, '日放电收益', 'Daily Discharge Income')} stroke="#52e8b0" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="linear" dataKey="actual" name={t(lang, '实际收益', 'Net Income')} stroke="#ffd166" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </Div>
  );
}

// ---------- 电量报表 ----------
function EnergyReport() {
  const { lang } = useLang();
  const eng = getEnergyReport();
  const charge = sum(eng, 'charge');
  const discharge = sum(eng, 'discharge');
  const efficiency = charge > 0 ? ((discharge / charge) * 100).toFixed(1) : '—';
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '电量报表', 'Energy Report')}</span><small>{t(lang, '日充电量 / 日放电量（储能表期末 − 期初之和）', 'Daily Charge / Daily Discharge (storage meter end − start sum)')}</small></div>
      <Table
        size="small" rowKey="key" pagination={false}
        columns={[
          { title: t(lang, '日期', 'Date'), dataIndex: 'day' },
          { title: t(lang, '日充电量 (kWh)', 'Daily Charge (kWh)'), dataIndex: 'charge', align: 'right' },
          { title: t(lang, '日放电量 (kWh)', 'Daily Discharge (kWh)'), dataIndex: 'discharge', align: 'right' },
          { title: t(lang, '系统效率 (%)', 'System Efficiency (%)'), render: (_, r) => (r.charge > 0 ? ((r.discharge / r.charge) * 100).toFixed(1) : '—'), align: 'right' },
        ]}
        dataSource={eng}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>{t(lang, '合计', 'Total')}</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">{charge}</Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">{discharge}</Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">{efficiency}</Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={eng} margin={{ top: 16, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="day" tick={tick} stroke={grid} />
          <YAxis tick={tick} stroke={grid} unit="kWh" width={64} />
          <RTooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={legendStyle} />
          <Line type="linear" dataKey="charge" name={t(lang, '日充电量', 'Daily Charge')} stroke="#4dd8ff" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="linear" dataKey="discharge" name={t(lang, '日放电量', 'Daily Discharge')} stroke="#52e8b0" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </Div>
  );
}

// ---------- 电表报表 ----------
function MeterReport() {
  const { lang } = useLang();
  const rows = getMeterReport();
  return (
    <>
      <Div>
        <div className="panel-title"><span>{t(lang, '电表电量（分时）', 'Meter Energy (TOU)')}</span><small>{t(lang, '峰 / 平 / 谷 分时电量', 'Peak / Flat / Valley TOU energy')}</small></div>
        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '日期', 'Date'), dataIndex: 'day' },
            { title: t(lang, '电表', 'Meter'), dataIndex: 'meter' },
            { title: t(lang, '峰 (kWh)', 'Peak (kWh)'), dataIndex: 'fp', align: 'right' },
            { title: t(lang, '平 (kWh)', 'Flat (kWh)'), dataIndex: 'ff', align: 'right' },
            { title: t(lang, '谷 (kWh)', 'Valley (kWh)'), dataIndex: 'fv', align: 'right' },
            { title: t(lang, '反向 (kWh)', 'Reverse (kWh)'), dataIndex: 'reverse', align: 'right' },
            { title: t(lang, '无功 (kVarh)', 'Reactive (kVarh)'), dataIndex: 'reactive', align: 'right' },
          ]}
          dataSource={rows}
        />
        <div className="ems-note">{t(lang, '日充电量 / 日放电量 = 储能表期末止度 − 期初止度之和。', 'Daily Charge / Daily Discharge = sum of storage meter end readings − start readings.')}</div>
      </Div>
      <Div>
        <div className="panel-title"><span>{t(lang, '电表止度', 'Meter Reading')}</span><small>{t(lang, '每日零时止度数据（止度 = 电能 / 变比）', 'Daily midnight reading (reading = energy / ratio)')}</small></div>
        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '日期', 'Date'), dataIndex: 'day' },
            { title: t(lang, '电表', 'Meter'), dataIndex: 'meter' },
            { title: t(lang, '电能示数 (kWh)', 'Energy Reading (kWh)'), dataIndex: 'energy', align: 'right' },
            { title: t(lang, '变比', 'Ratio'), dataIndex: 'ratio', align: 'right' },
            { title: t(lang, '止度', 'Reading'), dataIndex: 'stop', align: 'right', render: (v) => v.toFixed(3) },
          ]}
          dataSource={METER_STOP}
        />
      </Div>
    </>
  );
}

// ---------- 功率曲线 ----------
function PowerCurve({ st }) {
  const { lang } = useLang();
  const curve = getPowerCurve(st.id);
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '功率曲线', 'Power Curve')}</span><small>{t(lang, '今日 0 点 ~ 次日 6 点 · 电网 / 负载 / 储能 / 光伏功率 + 电价', 'Today 00:00 – next day 06:00 · Grid / Load / Storage / PV power + tariff')}</small></div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={curve} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="clock" tick={tick} interval={11} minTickGap={24} stroke={grid} />
          <YAxis yAxisId="p" tick={tick} stroke={grid} unit="kW" width={56} />
          <YAxis yAxisId="price" orientation="right" tick={tick} stroke={grid} unit="¥" width={48} />
          <RTooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={legendStyle} />
          <Line yAxisId="p" type="stepAfter" dataKey="grid" name={t(lang, '电网功率', 'Grid Power')} stroke="#4dd8ff" strokeWidth={2} dot={false} />
          <Line yAxisId="p" type="stepAfter" dataKey="load" name={t(lang, '负载功率', 'Load Power')} stroke="#ff5a7f" strokeWidth={2} dot={false} />
          <Line yAxisId="p" type="stepAfter" dataKey="storage" name={t(lang, '储能功率', 'Storage Power')} stroke="#52e8b0" strokeWidth={2} dot={false} />
          <Line yAxisId="p" type="stepAfter" dataKey="pv" name={t(lang, '光伏功率', 'PV Power')} stroke="#ffd166" strokeWidth={2} dot={false} />
          <Line yAxisId="price" type="stepAfter" dataKey="price" name={t(lang, '电价', 'Tariff')} stroke="#8b7cf6" strokeWidth={1.6} strokeDasharray="4 4" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="ems-note">{t(lang, '电价曲线采用阶梯（stepAfter）。', 'Tariff curve uses stepAfter.')}</div>
    </Div>
  );
}

// ---------- PCS 曲线 ----------
function PcsCurve() {
  const { lang } = useLang();
  const [pcsIdx, setPcsIdx] = useState(0);
  const [metric, setMetric] = useState('power');
  const [scope, setScope] = useState('compare');
  const two = getPcsCurve();
  const metricMap = {
    power: { key: 'dcPower', name: t(lang, '直流功率', 'DC Power'), unit: 'kW' },
    temp: { key: 'today', name: t(lang, '温度', 'Temp'), unit: '℃' },
    voltage: { key: 'aVoltage', name: t(lang, '三相电压', '3-Phase Voltage'), unit: 'V' },
    current: { key: 'aCurrent', name: t(lang, '三相电流', '3-Phase Current'), unit: 'A' },
  };
  const m = metricMap[metric];
  const data = scope === 'compare' ? two : PCS_TREND;
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, 'PCS 曲线', 'PCS Curve')}</span><small>{t(lang, '对比（近两日）/ 趋势（近七日），可切换设备与指标', 'Compare (last 2 days) / Trend (last 7 days), switch device & metric')}</small></div>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '设备', 'Device')}</span>
        <Select value={pcsIdx} onChange={setPcsIdx} style={{ width: 160 }} options={[{ value: 0, label: 'PCS-01' }, { value: 1, label: 'PCS-02' }]} />
        <Segmented value={scope} onChange={setScope} options={[{ value: 'compare', label: t(lang, '对比（近两日）', 'Compare (2d)') }, { value: 'trend', label: t(lang, '趋势（近七日）', 'Trend (7d)') }]} />
        <Segmented value={metric} onChange={setMetric} options={[{ value: 'power', label: t(lang, '功率', 'Power') }, { value: 'temp', label: t(lang, '温度', 'Temp') }, { value: 'voltage', label: t(lang, '三相电压', '3-Phase Voltage') }, { value: 'current', label: t(lang, '三相电流', '3-Phase Current') }]} />
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="clock" tick={tick} interval={Math.ceil(data.length / 12)} minTickGap={20} stroke={grid} />
          <YAxis tick={tick} stroke={grid} unit={m.unit} width={56} />
          <RTooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={legendStyle} />
          {scope === 'compare' && metric === 'temp' ? (
            <>
              <Line type="linear" dataKey="today" name={t(lang, '今日', 'Today')} stroke="#52e8b0" strokeWidth={2} dot={false} />
              <Line type="linear" dataKey="yesterday" name={t(lang, '昨日', 'Yesterday')} stroke="#4dd8ff" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </>
          ) : scope === 'compare' && metric === 'power' ? (
            <>
              <Line type="stepAfter" dataKey="dcPower" name={t(lang, '今日', 'Today')} stroke="#52e8b0" strokeWidth={2} dot={false} />
              <Line type="stepAfter" dataKey="dcPowerYesterday" name={t(lang, '昨日', 'Yesterday')} stroke="#4dd8ff" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </>
          ) : (
            <Line type={metric === 'power' || metric === 'current' ? 'stepAfter' : 'linear'} dataKey={scope === 'compare' ? m.key : metric} name={`${m.name}${scope === 'compare' ? t(lang, '（今日）', ' (today)') : ''}`} stroke="#52e8b0" strokeWidth={2} dot={false} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Div>
  );
}

// ---------- 电池堆曲线 ----------
function RackCurve() {
  const { lang } = useLang();
  const [metric, setMetric] = useState('soc');
  const [scope, setScope] = useState('compare');
  const seven = getRackCurve();
  const metricMap = {
    soc: { name: 'SOC', unit: '%', dataKey: 'soc' },
    voltage: { name: t(lang, '电压', 'Voltage'), unit: 'V', dataKey: 'voltage' },
    current: { name: t(lang, '电流', 'Current'), unit: 'A', dataKey: 'current' },
    temp: { name: t(lang, '温度', 'Temp'), unit: '℃', dataKey: 'tempMax' },
  };
  const m = metricMap[metric];
  const data = scope === 'compare' ? RACK_2D : seven;
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '电池堆曲线', 'Battery Rack Curve')}</span><small>{t(lang, '对比（近两日）/ 趋势（近七日）· 温度 / 电压 / 电流 / SOC', 'Compare (2d) / Trend (7d) · Temp / Voltage / Current / SOC')}</small></div>
      <div className="ems-inline">
        <Segmented value={scope} onChange={setScope} options={[{ value: 'compare', label: t(lang, '对比（近两日）', 'Compare (2d)') }, { value: 'trend', label: t(lang, '趋势（近七日）', 'Trend (7d)') }]} />
        <Segmented value={metric} onChange={setMetric} options={[{ value: 'soc', label: 'SOC' }, { value: 'voltage', label: t(lang, '电压', 'Voltage') }, { value: 'current', label: t(lang, '电流', 'Current') }, { value: 'temp', label: t(lang, '温度', 'Temp') }]} />
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="clock" tick={tick} interval={Math.ceil(data.length / 12)} minTickGap={20} stroke={grid} />
          <YAxis tick={tick} stroke={grid} unit={m.unit} width={56} />
          <RTooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={legendStyle} />
          {metric === 'temp' && scope === 'compare' ? (
            <>
              <Line type="linear" dataKey="tempMax" name={t(lang, '最高温度', 'Max Temp')} stroke="#ff5a7f" strokeWidth={2} dot={false} />
              <Line type="linear" dataKey="tempMin" name={t(lang, '最低温度', 'Min Temp')} stroke="#4dd8ff" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </>
          ) : (
            <Line type="linear" dataKey={m.dataKey} name={m.name} stroke="#52e8b0" strokeWidth={2} dot={false} />
          )}
        </LineChart>
      </ResponsiveContainer>
      <div className="ems-note">{t(lang, '本类型不含电池簇曲线（电池簇维度的对比曲线仅削峰填谷类型使用）。', 'This station type has no battery cluster curves (cluster-level comparison curves are only used by the peak-shaving type).')}</div>
    </Div>
  );
}

// ---------- 单体曲线 ----------
function CellCurve() {
  const { lang } = useLang();
  const [metric, setMetric] = useState('voltage');
  const [pick, setPick] = useState(String(CELL_PICKS[0]));
  const dataMap = { voltage: getCellCurve(), temp: CELL_TEMP, soc: CELL_SOC };
  const metaMap = {
    voltage: { nameZh: t(lang, '电压', 'Voltage'), nameEn: 'Voltage', unit: 'V' },
    temp: { nameZh: t(lang, '温度', 'Temp'), nameEn: 'Temp', unit: '℃' },
    soc: { nameZh: 'SOC', nameEn: 'SOC', unit: '%' },
  };
  const meta = metaMap[metric];
  const data = dataMap[metric];
  const lineName = t(lang, `${pick} 号单体${meta.nameZh}`, `Cell ${pick} ${meta.nameEn}`);
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '单体曲线', 'Cell Curve')}</span><small>{t(lang, '近七日 · 温度 / 电压 / SOC，可切换单体与时间段', 'Last 7 days · Temp / Voltage / SOC, switch cell & time range')}</small></div>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '单体', 'Cell')}</span>
        <Select value={pick} onChange={setPick} style={{ width: 140 }} options={CELL_PICKS.map((n) => ({ value: String(n), label: t(lang, `${n} 号单体`, `Cell ${n}`) }))} />
        <Segmented value={metric} onChange={setMetric} options={[{ value: 'temp', label: t(lang, '温度', 'Temp') }, { value: 'voltage', label: t(lang, '电压', 'Voltage') }, { value: 'soc', label: 'SOC' }]} />
        <Select defaultValue="7d" style={{ width: 150 }} options={[{ value: '7d', label: t(lang, '近七日', 'Last 7 days') }, { value: '30d', label: t(lang, '近三十日', 'Last 30 days') }]} />
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="clock" tick={tick} stroke={grid} />
          <YAxis tick={tick} stroke={grid} unit={meta.unit} width={56} />
          <RTooltip contentStyle={tooltipStyle} />
          <Line type="linear" dataKey={`c${pick}`} name={lineName} stroke="#52e8b0" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </Div>
  );
}

// ---------- 电池温度报表 ----------
function TempReport() {
  const { lang } = useLang();
  const rows = getClusterTempReport();
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '电池温度报表', 'Battery Temp Report')}</span><small>{t(lang, '整点时按电堆下所有电池簇统计，给出温度最高 / 最低单体及编号', 'Hourly stats across all battery clusters under the pack: max / min temp cell and its number')}</small></div>
      <Table
        size="small" rowKey="key" pagination={false}
        columns={[
          { title: t(lang, '电池簇', 'Cluster'), dataIndex: 'cluster' },
          { title: t(lang, '最高温度 (℃)', 'Max Temp (℃)'), dataIndex: 'max', align: 'right' },
          { title: t(lang, '最高单体', 'Max Cell'), dataIndex: 'maxCell' },
          { title: t(lang, '最低温度 (℃)', 'Min Temp (℃)'), dataIndex: 'min', align: 'right' },
          { title: t(lang, '最低单体', 'Min Cell'), dataIndex: 'minCell' },
          { title: t(lang, '平均温度 (℃)', 'Avg Temp (℃)'), dataIndex: 'avg', align: 'right' },
          { title: t(lang, '采样时间', 'Sample Time'), dataIndex: 'time', align: 'right' },
        ]}
        dataSource={rows}
      />
    </Div>
  );
}

// ---------- 电池电压报表 ----------
function VoltageReport() {
  const { lang } = useLang();
  const rows = getClusterVoltageReport();
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '电池电压报表', 'Battery Voltage Report')}</span><small>{t(lang, '整点时按电堆下所有电池簇统计，给出电压最高 / 最低单体及编号', 'Hourly stats across all battery clusters under the pack: max / min voltage cell and its number')}</small></div>
      <Table
        size="small" rowKey="key" pagination={false}
        columns={[
          { title: t(lang, '电池簇', 'Cluster'), dataIndex: 'cluster' },
          { title: t(lang, '最高电压 (V)', 'Max Voltage (V)'), dataIndex: 'max', align: 'right' },
          { title: t(lang, '最低电压 (V)', 'Min Voltage (V)'), dataIndex: 'min', align: 'right' },
          { title: t(lang, '平均电压 (V)', 'Avg Voltage (V)'), dataIndex: 'avg', align: 'right' },
          { title: t(lang, '压差 (V)', 'Diff (V)'), dataIndex: 'diff', align: 'right' },
          { title: t(lang, '采样时间', 'Sample Time'), dataIndex: 'time', align: 'right' },
        ]}
        dataSource={rows}
      />
    </Div>
  );
}

// ---------- 自定义曲线 ----------
function CustomCurve({ st, exports, setExports }) {
  const { lang } = useLang();
  const curve = getPowerCurve(st.id);
  const [points, setPoints] = useState(['storage', 'grid']);
  const [range, setRange] = useState('7d');
  const [freq, setFreq] = useState('1h');
  const series = CUSTOM_POINTS.filter((p) => points.includes(p.key));
  const hasPrice = points.includes('price');
  const POINT_EN = { 电网功率: 'Grid Power', 负载功率: 'Load Power', 储能功率: 'Storage Power', SOC: 'SOC', 电价: 'Tariff' };
  const FREQ_EN = { '15 分钟': '15 min', '1 小时': '1 hour', '1 天': '1 day' };
  const onExport = () => {
    const size = `${(curve.length * series.length / 12).toFixed(1)} KB`;
    setExports([{ key: Date.now(), name: `自定义曲线_${range}_${freq}.xlsx`, type: `自定义曲线（${series.length} 测点）`, time: '2026-10-17 10:05', state: '已完成', size }, ...exports]);
    message.success(t(lang, '导出任务已提交，可在导出列表查看', 'Export task submitted; check the export list'));
  };
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '自定义曲线', 'Custom Curve')}</span><small>{t(lang, '添加不同测点、选择时间范围、导出不同频率数据', 'Add different points, choose a time range, export data at different frequencies')}</small></div>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '测点', 'Points')}</span>
        <Select
          mode="multiple" allowClear value={points} onChange={setPoints} style={{ minWidth: 320 }}
          options={CUSTOM_POINTS.map((p) => ({ value: p.key, label: `${t(lang, p.label, POINT_EN[p.label])} (${p.unit})` }))}
        />
        <span className="ems-inline-label">{t(lang, '时间范围', 'Time Range')}</span>
        <Select value={range} onChange={setRange} style={{ width: 130 }} options={[{ value: '1d', label: t(lang, '近 1 天', 'Last 1 day') }, { value: '7d', label: t(lang, '近 7 天', 'Last 7 days') }, { value: '30d', label: t(lang, '近 30 天', 'Last 30 days') }]} />
        <span className="ems-inline-label">{t(lang, '导出频率', 'Export Freq')}</span>
        <Select value={freq} onChange={setFreq} style={{ width: 130 }} options={CUSTOM_FREQ.map((f) => ({ value: f.value, label: t(lang, f.label, FREQ_EN[f.label]) }))} />
        <Button type="primary" className="panel-title-btn" onClick={onExport}>{t(lang, '导出', 'Export')}</Button>
      </div>
      {series.length === 0 ? (
        <div className="ems-note">{t(lang, '请至少选择一个测点。', 'Please select at least one point.')}</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={curve} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="clock" tick={tick} interval={11} minTickGap={24} stroke={grid} />
            <YAxis yAxisId="l" tick={tick} stroke={grid} width={56} />
            <YAxis yAxisId="r" orientation="right" tick={tick} stroke={grid} unit="¥" width={48} />
            <RTooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={legendStyle} />
            {series.filter((s) => s.key !== 'price').map((s) => (
              <Line key={s.key} yAxisId="l" type={s.unit === 'kW' || s.unit === 'kVar' ? 'stepAfter' : 'linear'} dataKey={s.key} name={`${t(lang, s.label, POINT_EN[s.label])} (${s.unit})`} stroke={s.color} strokeWidth={2} dot={false} />
            ))}
            {hasPrice ? <Line yAxisId="r" type="stepAfter" dataKey="price" name={`${t(lang, '电价', 'Tariff')} (¥/kWh)`} stroke="#8b7cf6" strokeWidth={1.6} dot={false} /> : null}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Div>
  );
}

// ---------- 导出列表 ----------
function ExportList({ exports }) {
  const { lang } = useLang();
  return (
    <Div>
      <div className="panel-title"><span>{t(lang, '导出列表', 'Export List')}</span><small>{t(lang, '异步导出任务', 'Async export tasks')}</small></div>
      <Table
        size="small" rowKey="key" pagination={false}
        columns={[
          { title: t(lang, '文件名', 'File Name'), dataIndex: 'name' },
          { title: t(lang, '类型', 'Type'), dataIndex: 'type' },
          { title: t(lang, '生成时间', 'Generated At'), dataIndex: 'time' },
          { title: t(lang, '状态', 'Status'), dataIndex: 'state', render: (v) => <Tag color={v === '已完成' ? 'green' : 'gold'}>{t(lang, v, v === '已完成' ? 'Completed' : 'Generating')}</Tag> },
          { title: t(lang, '大小', 'Size'), dataIndex: 'size', align: 'right' },
          { title: t(lang, '操作', 'Action'), render: (_, r) => r.state === '已完成'
            ? <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => message.success(t(lang, '下载已开始（演示）', 'Download started (demo)'))}>{t(lang, '下载', 'Download')}</Button>
            : <span style={{ color: 'rgba(255,255,255,0.35)' }}>—</span> },
        ]}
        dataSource={exports}
      />
    </Div>
  );
}

export default function EmsCurve({ station }) {
  const { lang } = useLang();
  const st = station || getStation('xz');
  const [exports, setExports] = useState(() => getExportList());
  const items = [
    { key: 'overview', label: t(lang, '概览统计', 'Overview'), children: <Overview st={st} /> },
    { key: 'income', label: t(lang, '收益报表', 'Income Report'), children: <IncomeReport /> },
    { key: 'energy', label: t(lang, '电量报表', 'Energy Report'), children: <EnergyReport /> },
    { key: 'meter', label: t(lang, '电表报表', 'Meter Report'), children: <MeterReport /> },
    { key: 'power', label: t(lang, '功率曲线', 'Power Curve'), children: <PowerCurve st={st} /> },
    { key: 'pcs', label: t(lang, 'PCS 曲线', 'PCS Curve'), children: <PcsCurve /> },
    { key: 'rack', label: t(lang, '电池堆曲线', 'Battery Rack Curve'), children: <RackCurve /> },
    { key: 'cell', label: t(lang, '单体曲线', 'Cell Curve'), children: <CellCurve /> },
    { key: 'temp', label: t(lang, '电池温度报表', 'Battery Temp Report'), children: <TempReport /> },
    { key: 'volt', label: t(lang, '电池电压报表', 'Battery Voltage Report'), children: <VoltageReport /> },
    { key: 'custom', label: t(lang, '自定义曲线', 'Custom Curve'), children: <CustomCurve st={st} exports={exports} setExports={setExports} /> },
    { key: 'export', label: t(lang, '导出列表', 'Export List'), children: <ExportList exports={exports} /> },
  ];
  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return <ScrollTabs items={items} />;
}
